import { formatIdentificationDocument } from "@/utils/formatDocument";
import api from "@/shared/services/api";
import {
  ClientListUserResponse,
  ClientRow,
  ClientStatistics,
  NewClientsRange,
  RegistrationListAttribution,
  RegistrationListOption,
  ResidenceStateOption,
  StateFilter,
  emptyClientLevelBreakdown,
  emptyIndicatorBreakdown,
  emptyNewClientsBreakdown,
  getEmptyClientStatistics,
} from "../types/clientList.types";
import { formatClientPhone } from "../utils/clientListFormatters";

type LocationResponse = {
  code?: string | number;
  name?: string;
};

export interface ClientListResult {
  users: ClientRow[];
  registrationListOptions: RegistrationListOption[];
}

const getRegistrationLists = (
  value: unknown,
): RegistrationListAttribution[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (entry): entry is RegistrationListAttribution =>
      Boolean(entry) &&
      typeof entry === "object" &&
      typeof (entry as RegistrationListAttribution).key === "string" &&
      typeof (entry as RegistrationListAttribution).label === "string",
  );
};

const mapClientRow = (user: ClientListUserResponse): ClientRow => ({
  id: user._id,
  name: `${user.name ?? ""} ${user.lastname ?? ""}`.trim(),
  email: user.email ?? "",
  phone: formatClientPhone(user.phone),
  document: formatIdentificationDocument(
    user.identificationType,
    user.document,
  ),
  identificationType: user.identificationType ?? "",
  documentNumber: String(user.document ?? ""),
  status: user.status ?? "",
  statusName: user.statusName ?? "",
  debtState: user.debtState ?? "inactivo",
  debtStateName: user.debtStateName ?? "Inactivo",
  image: user.image ?? null,
  registrationLists: getRegistrationLists(user.registrationLists),
  missingProfilePicture: Boolean(user.missingProfilePicture),
  missingDocumentPicture: Boolean(user.missingDocumentPicture),
  maxAmount: user.maxAmount ?? null,
  creditoDisponible: user.creditoDisponible ?? null,
  creditoUsado: user.creditoUsado ?? null,
  cuotasVencidas: user.cuotasVencidas ?? 0,
  montoVencidoUsd: user.montoVencidoUsd ?? 0,
  lastPaymentAt: user.lastPaymentAt ?? null,
  daysInArrears: user.daysInArrears ?? 0,
  createdAt: user.createdAt ?? null,
});

const normalizeStatistics = (value: any): ClientStatistics => {
  const empty = getEmptyClientStatistics();
  const data = value || {};

  return {
    total: data.total || data.totales || empty.total,
    vigentes: data.vigentes || empty.vigentes,
    inactivos: data.inactivos || empty.inactivos,
    vencidos: data.vencidos || empty.vencidos,
    morosos: data.morosos || empty.morosos,
    morososSinGrupo: data.morososSinGrupo || empty.morososSinGrupo,
    suspendidos: data.suspendidos || empty.suspendidos,
    suspendidosPercentage:
      data.suspendidosPercentage || empty.suspendidosPercentage,
    newClients: {
      ...emptyNewClientsBreakdown,
      lastMonth: data.newClients?.lastMonth || 0,
      lastWeek: data.newClients?.lastWeek || 0,
      yesterday: data.newClients?.yesterday || 0,
      today: data.newClients?.today || 0,
    },
    levels: {
      ...emptyClientLevelBreakdown,
      total: data.levels?.total || 0,
      level1: data.levels?.level1 || 0,
      level2: data.levels?.level2 || 0,
      level3: data.levels?.level3 || 0,
      level4: data.levels?.level4 || 0,
      level5: data.levels?.level5 || 0,
    },
    amounts: {
      ...emptyIndicatorBreakdown,
      total: data.amounts?.total || 0,
      vigentes: data.amounts?.vigentes || 0,
      inactivos: data.amounts?.inactivos || 0,
      vencidos: data.amounts?.vencidos || 0,
      morosos: data.amounts?.morosos || 0,
    },
    portfolioDistributionAmounts: {
      ...emptyIndicatorBreakdown,
      total: data.portfolioDistributionAmounts?.total || 0,
      vigentes: data.portfolioDistributionAmounts?.vigentes || 0,
      inactivos: data.portfolioDistributionAmounts?.inactivos || 0,
      vencidos: data.portfolioDistributionAmounts?.vencidos || 0,
      morosos: data.portfolioDistributionAmounts?.morosos || 0,
    },
    amountPercentages: {
      ...emptyIndicatorBreakdown,
      total: data.amountPercentages?.total || 0,
      vigentes: data.amountPercentages?.vigentes || 0,
      inactivos: data.amountPercentages?.inactivos || 0,
      vencidos: data.amountPercentages?.vencidos || 0,
      morosos: data.amountPercentages?.morosos || 0,
    },
    amountDetails: {
      morosos: {
        cuotasMorosas: {
          amountUsd: data.amountDetails?.morosos?.cuotasMorosas?.amountUsd || 0,
          percentage:
            data.amountDetails?.morosos?.cuotasMorosas?.percentage || 0,
        },
        cuotasVigentes: {
          count: data.amountDetails?.morosos?.cuotasVigentes?.count || 0,
          amountUsd: data.amountDetails?.morosos?.cuotasVigentes?.amountUsd || 0,
          percentage:
            data.amountDetails?.morosos?.cuotasVigentes?.percentage || 0,
        },
        cuotasVencidas: {
          count: data.amountDetails?.morosos?.cuotasVencidas?.count || 0,
          amountUsd: data.amountDetails?.morosos?.cuotasVencidas?.amountUsd || 0,
          percentage:
            data.amountDetails?.morosos?.cuotasVencidas?.percentage || 0,
        },
      },
    },
    percentages: {
      ...emptyIndicatorBreakdown,
      total: data.percentages?.total || 0,
      vigentes: data.percentages?.vigentes || 0,
      inactivos: data.percentages?.inactivos || 0,
      vencidos: data.percentages?.vencidos || 0,
      morosos: data.percentages?.morosos || 0,
    },
  };
};

const getLocationData = (value: any): LocationResponse[] =>
  Array.isArray(value?.data) ? (value.data as LocationResponse[]) : [];

const toResidenceStateOptions = (
  locations: LocationResponse[],
): ResidenceStateOption[] =>
  locations.reduce<ResidenceStateOption[]>((options, location) => {
    const label = location.name?.trim();
    const value =
      location.code !== undefined && location.code !== null
        ? String(location.code).trim()
        : label;

    if (!label || !value) return options;

    return [...options, { value, label }];
  }, []);

const toRegistrationListOptions = (options: unknown): RegistrationListOption[] => {
  if (!Array.isArray(options)) return [];

  return options.reduce<RegistrationListOption[]>((items, option) => {
    const key = typeof option?.key === "string" ? option.key.trim() : "";
    const label = typeof option?.label === "string" ? option.label.trim() : key;
    const totalUsers = Number(option?.totalUsers ?? 0);

    if (!key || !label) return items;

    return [
      ...items,
      {
        value: key,
        label: totalUsers > 0 ? `${label} (${totalUsers})` : label,
        totalUsers,
      },
    ];
  }, []);
};

type ClientListServiceEndpoints = {
  list: string;
  statistics: string;
  updateStatus: (userId: string) => string;
};

const defaultEndpoints: ClientListServiceEndpoints = {
  list: "/users/get-all",
  statistics: "/users/statistics",
  updateStatus: (userId: string) => `/users/${userId}/status`,
};

const createClientListService = (endpoints = defaultEndpoints) => ({
  async getClients(
    state: StateFilter,
    newClientsRange?: NewClientsRange | null,
    residenceState?: string | null,
    registrationListKey?: string | null,
    missingPhotos?: boolean,
    globalFilter?: string | null,
  ): Promise<ClientListResult> {
    const params: Record<string, string> = {};

    if (newClientsRange) {
      params.newClientsRange = newClientsRange;
    } else if (state !== "total") {
      params.state = state;
    }

    if (residenceState) {
      params.residenceState = residenceState;
    }

    if (registrationListKey) {
      params.registrationListKey = registrationListKey;
    }

    if (missingPhotos) {
      params.missingPhotos = "true";
    }

    if (globalFilter?.trim()) {
      params.globalFilter = globalFilter.trim();
    }

    const response = await api.get(endpoints.list, { params });
    const users = Array.isArray(response.data?.data) ? response.data.data : [];

    return {
      users: users.map(mapClientRow),
      registrationListOptions: toRegistrationListOptions(
        response.data?.registrationListOptions,
      ),
    };
  },

  async getResidenceStates(): Promise<ResidenceStateOption[]> {
    const countriesResponse = await api.get("/locations/countries");
    const countries = getLocationData(countriesResponse.data);
    const venezuela = countries.find((country) =>
      country.name?.toLowerCase().includes("venezuela"),
    );

    if (venezuela?.code === undefined || venezuela.code === null) return [];

    const statesResponse = await api.get("/locations/states", {
      params: { country: String(venezuela.code) },
    });

    return toResidenceStateOptions(getLocationData(statesResponse.data));
  },

  async getStatistics(): Promise<ClientStatistics> {
    const response = await api.get(endpoints.statistics);
    return normalizeStatistics(response.data?.data || response.data || {});
  },

  async getStatisticsByCreditPolicy(
    creditPolicy: string,
  ): Promise<ClientStatistics> {
    const response = await api.get(endpoints.statistics, {
      params: { creditPolicy },
    });
    return normalizeStatistics(response.data?.data || response.data || {});
  },

  async getStatisticsByResidenceState(
    residenceState?: string | null,
    registrationListKey?: string | null,
  ): Promise<ClientStatistics> {
    const params: Record<string, string> = {};

    if (residenceState) params.residenceState = residenceState;
    if (registrationListKey) params.registrationListKey = registrationListKey;

    const response = await api.get(endpoints.statistics, { params });
    return normalizeStatistics(response.data?.data || response.data || {});
  },

  async updateStatus(userId: string, status: string) {
    const response = await api.patch(endpoints.updateStatus(userId), { status });
    return response.data;
  },
});

const clientListService = createClientListService();

export const creditLineClientListService = createClientListService({
  list: "/credit-line/users/get-all",
  statistics: "/credit-line/users/statistics",
  updateStatus: (userId: string) => `/credit-line/users/${userId}/status`,
});

export type ClientListService = typeof clientListService;

export default clientListService;
